'use client';
import { create } from 'zustand';
import { colors } from '@/constant/colors';
import { Task } from '@/constant/tasks';
import Image from '@/components/Image';
import { useEffect, useRef } from 'react';
import { getAssetUrl } from '@/constant/assets';
import NormalButton from '../Button/normal';
import report, { ReportEvent } from '@/lib/report';

interface FinishTaskStore {
  show: boolean;
  showToast: (text: string, diamond: number) => void;
  hideToast: () => void;
  text: string;
  diamond: number;
}

const useFinishTaskStore = create<FinishTaskStore>((set, get) => ({
  show: false,
  text: '',
  diamond: 0,
  showToast: (text: string, diamond: number) => set({ show: true, text: text, diamond: diamond }),
  hideToast: () => {
    report(ReportEvent.USER_COMPLETE_DAILY_TASK, {
      diamond: get().diamond || 0,
    });
    set({ show: false });
  },
}));

export default function FinishTask() {
  const { show, hideToast, text, diamond } = useFinishTaskStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (show) {
      audioRef.current = new Audio(getAssetUrl('assets/audio/task-finish.wav'));
      audioRef.current.volume = 0.3;
      audioRef.current.play();
    }
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [show]);

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      <div className="absolute inset-0 bg-white/90" />
      <div className="flex flex-col items-center justify-center relative gap-4">
        <div className="text-2xl emphasize-text mt-4 text-center" style={{ color: '#373D43' }}>
          {text.split('\n').map((line, i) => (
            <div key={i}>{line}</div>
          ))}
        </div>
        <div>
          <Image 
            src="/assets/pengi_finish_task.png" 
            alt="pengi_finish_task" 
            width={113} 
            height={143} 
            className="w-[220px] h-[280px] object-contain"
            optimizedWidth={500}
          />
        </div>
        <div className="flex flex-row items-center justify-center mt-4 text-center text-2xl" style={{ color: colors.secondaryColor }}>
          <Image src="/assets/diamond-3.png" alt="diamond" width={20} height={20} className="w-12 h-12" />
          <span className="ml-2 text-[#735648] text-5xl emphasize-text">+{diamond}</span>
        </div>
        <NormalButton onClick={hideToast} className='mt-4'>
          Claim
        </NormalButton>
      </div>
    </div>
  );
}

export const useFinishTask = () => {
  const { showToast, hideToast } = useFinishTaskStore();
  
  const showTaskToast = (tasks: Task[]) => {
    let text = '';
    if (tasks.length == 1) {
      text = 'You\'ve completed a daily task:';
    } else {
      text = 'You\'ve completed daily tasks:';
    }
    text += '\n' + tasks.map(task => task.description).join('\n');
    const diamond = tasks.reduce((acc, task) => acc + task.rewardDiamond, 0);
    
    setTimeout(() => {
      showToast(text, diamond);
    }, 2000);
  };

  return { showTaskToast };
};
